import React from "react";
import { Alert, Container, Spinner } from "reactstrap";

import { BetHistoryPeriod, getMyBetHistory } from "../../../helpers/api/bet";
import type { UserBetHistoryItem } from "../../../types/api.generated";

export interface UserBetHistoryPoint {
  date: string;
  value: number;
}

const periodOptions: Array<{ key: BetHistoryPeriod; label: string }> = [
  { key: "weekly", label: "Weekly" },
  { key: "monthly", label: "Monthly" },
  { key: "all", label: "All" },
];

export const getBetStake = (bet: UserBetHistoryItem) => Number(bet.stakedPoints);

export const getBetPayout = (bet: UserBetHistoryItem) => Number(bet.payoutPoints);

export const getBetNet = (bet: UserBetHistoryItem) => Number(bet.netProfit);

export const isWinningBet = (bet: UserBetHistoryItem) => {
  if (["won", "paid"].includes(bet.status)) return true;
  if (["lost", "refunded"].includes(bet.status)) return false;
  return bet.netProfit > 0;
};

export const getWinRateValue = (bets: UserBetHistoryItem[]) => {
  if (bets.length === 0) return 0;
  return (bets.filter(isWinningBet).length / bets.length) * 100;
};

export const buildBetStats = (bets: UserBetHistoryItem[]) => {
  const wins = bets.filter(isWinningBet).length;
  const losses = Math.max(0, bets.length - wins);
  const bestBet = bets.reduce<UserBetHistoryItem | undefined>((best, bet) => {
    if (!best) return bet;
    return getBetNet(bet) > getBetNet(best) ? bet : best;
  }, undefined);

  return {
    betsPlaced: bets.length,
    winRate: getWinRateValue(bets),
    wins,
    losses,
    profitTotal: bets.reduce((total, bet) => total + getBetNet(bet), 0),
    bestGain: bestBet ? getBetNet(bestBet) : 0,
    bestBetId: bestBet?.battleId ?? bestBet?.id,
    since: bets[bets.length - 1]?.createdAt,
  };
};

const formatInteger = (value: number) => new Intl.NumberFormat("fr-FR").format(Math.round(value));

const formatSigned = (value: number) => `${value > 0 ? "+" : ""}${formatInteger(value)}`;

const formatPercent = (value: number) => `${Math.round(Math.max(0, Math.min(100, value)))}%`;

const formatDate = (value?: string) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";

  return new Intl.DateTimeFormat("fr-FR", {
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
};

const getBetBattleId = (bet: UserBetHistoryItem) => bet.battleId;

const getBattleLabel = (bet: UserBetHistoryItem) => bet.battleTitle;

export const getBattleParticipantNames = (bet: UserBetHistoryItem) => {
  return bet.characters.map((character) => character.name).join(" vs ");
};

const getStatusLabel = (bet: UserBetHistoryItem) => (isWinningBet(bet) ? "Won" : "Lost");

const getStatusClass = (bet: UserBetHistoryItem) => `bet-history-status ${isWinningBet(bet) ? "bet-history-status--won" : "bet-history-status--lost"}`;

const getChartValue = (point: UserBetHistoryPoint) => Number(point.value);

const StatCard = ({ label, value, detail, tone }: { label: string; value: string; detail: string; tone?: "positive" | "negative" }) => (
  <article className="bet-history-stat">
    <span>{label}</span>
    <strong className={tone === "positive" ? "is-positive" : tone === "negative" ? "is-negative" : undefined}>{value}</strong>
    <small>{detail}</small>
  </article>
);

const BetHistoryChart = ({ points }: { points: UserBetHistoryPoint[] }) => {
  const values = points.map(getChartValue);
  const safeValues = values.length > 0 ? values : [0];
  const min = Math.min(...safeValues, 0);
  const max = Math.max(...safeValues, 1);
  const range = max - min || 1;
  const width = 720;
  const height = 170;
  const xStep = safeValues.length > 1 ? width / (safeValues.length - 1) : width;
  const coordinates = safeValues.map((value, index) => {
    const x = index * xStep;
    const y = height - ((value - min) / range) * (height - 22) - 11;
    return `${x},${y}`;
  });
  const area = `0,${height} ${coordinates.join(" ")} ${width},${height}`;
  const latest = safeValues[safeValues.length - 1] ?? 0;

  return (
    <div className="bet-history-chart__frame" data-testid="bet-history-chart">
      <div className="bet-history-chart__summary">
        <span>Solde</span>
        <strong className={latest >= 0 ? "is-positive" : "is-negative"}>{formatSigned(latest)} PTS</strong>
      </div>
      <svg viewBox={`0 0 ${width} ${height}`} role="img" aria-label="Bet profit evolution">
        <defs>
          <linearGradient id="bet-history-area" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%" stopColor="rgba(0, 255, 255, 0.5)" />
            <stop offset="100%" stopColor="rgba(0, 255, 255, 0)" />
          </linearGradient>
        </defs>
        <polygon points={area} fill="url(#bet-history-area)" />
        <polyline points={coordinates.join(" ")} fill="none" stroke="var(--c-cyan)" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </div>
  );
};

const BetHistory = () => {
  document.title = "Legends | The Colosseum - My Bets";

  const [period, setPeriod] = React.useState<BetHistoryPeriod>("all");
  const [bets, setBets] = React.useState<UserBetHistoryItem[]>([]);
  const [chart, setChart] = React.useState<UserBetHistoryPoint[]>([]);
  const [total, setTotal] = React.useState<number | undefined>(undefined);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    let active = true;

    const loadHistory = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await getMyBetHistory({ period });
        if (!active) return;
        setBets(response.items);
        setChart([]);
        setTotal(response.meta.totalItems);
      } catch (e: any) {
        if (active) setError(typeof e === "string" ? e : e?.message || "Failed to load bet history");
      } finally {
        if (active) setLoading(false);
      }
    };

    loadHistory();

    return () => {
      active = false;
    };
  }, [period]);

  const computedStats = React.useMemo(() => buildBetStats(bets), [bets]);
  const chartPoints = React.useMemo(() => {
    if (chart.length > 0) return chart;

    return bets.slice().reverse().reduce<UserBetHistoryPoint[]>((points, bet) => {
      const previous = points[points.length - 1]?.value ?? 0;
      points.push({ date: bet.createdAt, value: previous + getBetNet(bet) });
      return points;
    }, []);
  }, [bets, chart]);
  const displayedTotal = total ?? computedStats.betsPlaced;

  return (
    <div className="page-content bet-history-page" data-cy="bet-history-page">
      <Container fluid>
        <section className="bet-history-stats" aria-label="Bet statistics">
          <StatCard label="Paris placés" value={formatInteger(computedStats.betsPlaced)} detail={computedStats.since ? `Depuis ${formatDate(computedStats.since)}` : `${displayedTotal} paris au total`} />
          <StatCard label="Taux de victoire" value={formatPercent(computedStats.winRate)} detail={`${formatInteger(computedStats.wins)} W · ${formatInteger(computedStats.losses)} L`} />
          <StatCard label="Profit total" value={`${formatSigned(computedStats.profitTotal)} PTS`} detail="Toutes périodes" tone={computedStats.profitTotal >= 0 ? "positive" : "negative"} />
          <StatCard label="Meilleur gain" value={`${formatInteger(computedStats.bestGain)} PTS`} detail={`#${computedStats.bestBetId ?? "-"}`} tone={computedStats.bestGain >= 0 ? "positive" : "negative"} />
        </section>

        <section className="bet-history-chart">
          <div className="bet-history-period-tabs" role="tablist" aria-label="Bet history period">
            {periodOptions.map((option) => (
              <button
                type="button"
                key={option.key}
                className={period === option.key ? "is-active" : ""}
                onClick={() => setPeriod(option.key)}
                data-cy={`bet-history-period-${option.key}`}
                role="tab"
                aria-selected={period === option.key}
              >
                {option.label}
              </button>
            ))}
          </div>
          <BetHistoryChart points={chartPoints} />
        </section>

        <section className="bet-history-table-card">
          <header className="bet-history-table-card__header">
            <h1>History</h1>
            <span>{formatInteger(displayedTotal)} paris</span>
          </header>

          {error && <Alert color="danger" role="alert">{error}</Alert>}

          <div className="bet-history-table-wrap">
            <table className="bet-history-table">
              <thead>
                <tr>
                  <th>Battle</th>
                  <th>Mise</th>
                  <th>Payout</th>
                  <th>Net</th>
                  <th>Statut</th>
                  <th>Bet At</th>
                  <th>Battle start At</th>
                </tr>
              </thead>
              <tbody>
                {bets.map((bet, index) => {
                  const net = getBetNet(bet);
                  const participantNames = getBattleParticipantNames(bet);
                  return (
                    <tr data-cy="bet-history-row" data-testid="bet-history-row" key={bet.id || `${bet.battleId}-${index}`}>
                      <td className="bet-history-battle-cell">
                        <strong>{getBattleLabel(bet)}</strong>
                        <small>{participantNames || getBetBattleId(bet)}</small>
                      </td>
                      <td>{formatInteger(getBetStake(bet))}</td>
                      <td>{formatInteger(getBetPayout(bet))}</td>
                      <td className={net >= 0 ? "is-positive" : "is-negative"}>{formatSigned(net)}</td>
                      <td><span className={getStatusClass(bet)}>{getStatusLabel(bet)}</span></td>
                      <td>{formatDate(bet.createdAt)}</td>
                      <td>{formatDate(bet.battleStartTime)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {loading && (
            <div className="bet-history-state" data-cy="bet-history-loading">
              <Spinner size="sm" /> Loading bets...
            </div>
          )}

          {!loading && bets.length === 0 && !error && (
            <div className="bet-history-state" data-cy="bet-history-empty" data-testid="bet-history-empty">No bets found</div>
          )}
        </section>
      </Container>
    </div>
  );
};

export default BetHistory;
